import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  message,
  Space,
  Typography,
  Modal,
  Segmented, // Import Segmented
} from "antd";
import { LockOutlined } from "@ant-design/icons";
import PhoneInput from "react-phone-number-input"; // Import PhoneInput
import "react-phone-number-input/style.css"; // Import PhoneInput styles
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { loginUser, clearError } from "../../store/authSlice";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./AuthForms.scss";
import Logo from "../../asset/images/logo.png";

const { Text } = Typography;

interface LoginCredentials {
  identifier: string;
  password: string;
  login_type: "email" | "phone";
}

interface LoginFormData {
  identifier: string;
  password: string;
  login_type: string; // Add login_type to the form data
}

const LoginForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, error, isAuthenticated } = useAppSelector(
    (state) => state.auth
  );
  const [form] = Form.useForm();
  const [showInstructions, setShowInstructions] = useState(false);
  const [loginType, setLoginType] = useState<"email" | "phone">("phone"); // New state for login type
  const [phoneValue, setPhoneValue] = useState<string>(); // New state for phone number

  // Get the redirect path from location state, default to home
  const from = (location.state as any)?.from?.pathname || "/";

  useEffect(() => {
    // If already authenticated, redirect immediately
    if (isAuthenticated) {
      navigate(from, { replace: true });
    } else {
      // Check if this is a page refresh (not navigation)
      const hasShownModal = sessionStorage.getItem("loginModalShown");
      const navigationEntry = performance.getEntriesByType(
        "navigation"
      )[0] as PerformanceNavigationTiming;
      const isPageRefresh =
        performance.navigation?.type === 1 ||
        navigationEntry?.type === "reload";

      // Show modal only on page refresh or if never shown in this session
      if (isPageRefresh || !hasShownModal) {
        const timer = setTimeout(() => {
          setShowInstructions(true);
          sessionStorage.setItem("loginModalShown", "true");
        }, 1000); // Show after 1 second

        return () => clearTimeout(timer);
      }
    }
  }, [isAuthenticated, navigate, from]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleSubmit = async (values: LoginFormData) => {
    try {
      let obj = {
        login_type: loginType, // Use the state variable here
        identifier: loginType === "phone" ? phoneValue : values.identifier, // Use phoneValue if loginType is phone
        password: values.password,
      };
      const result = await dispatch(
        loginUser(obj as LoginCredentials)
      ).unwrap();
      message.success(result.message || "Login successful!");
      // Navigation will happen via useEffect when isAuthenticated becomes true
    } catch (err) {
      // Error is handled by Redux and shown via useEffect
    }
  };

  // Validator for phone number input
  const validatePhone = () => {
    if (loginType !== "phone") return Promise.resolve(); // Only validate if phone login is selected

    if (!phoneValue) {
      return Promise.reject(new Error("Please enter your phone number"));
    }

    const digitsOnly = phoneValue.replace(/\D/g, "");

    if (digitsOnly.length < 10) {
      return Promise.reject(
        new Error("Phone number must be at least 10 digits")
      );
    }

    if (digitsOnly.length > 15) {
      return Promise.reject(new Error("Phone number is too long"));
    }

    if (!phoneValue.startsWith("+")) {
      return Promise.reject(new Error("Please select a valid country code"));
    }

    if (phoneValue.startsWith("+91")) {
      const indianNumber = digitsOnly.substring(2);
      if (indianNumber.length !== 10) {
        return Promise.reject(
          new Error("Indian mobile number must be 10 digits")
        );
      }
      if (!["6", "7", "8", "9"].includes(indianNumber.charAt(0))) {
        return Promise.reject(new Error("Invalid Indian mobile number format"));
      }
    }

    return Promise.resolve();
  };

  return (
    <div className="auth-container">
      <Card className="auth-card">
        <div className="auth-header">
          {/* <Title level={2} className="auth-title">
            സുപ്രഭാതം ഇ-പേപ്പർ
          </Title> */}
          <br />
          <img src={Logo} style={{ height: "35px", marginBottom: "10px" }} />
          <br />
          {/* <br /> */}
          <Text className="auth-subtitle">Sign in to your account</Text>
        </div>

        {/* Login Type Selector */}
        <Segmented
          block
          options={[
            { label: "Phone Login", value: "phone" },
            { label: "Email Login", value: "email" },
          ]}
          value={loginType}
          onChange={(value) => {
            setLoginType(value as "email" | "phone");
            form.setFieldsValue({ identifier: "" }); // Clear identifier when switching type
          }}
          style={{ marginBottom: 24 }}
          className="login-type-segmented"
        />

        <Form
          form={form}
          name="login"
          onFinish={handleSubmit}
          layout="vertical"
          size="middle"
          className="auth-form"
        >
          {loginType === "email" ? (
            <Form.Item
              name="identifier"
              label="Email"
              rules={[
                {
                  required: true,
                  message: "Please enter your email",
                },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input
                style={{ borderRadius: "0px" }}
                // prefix={<UserOutlined />}
                placeholder="Enter your email"
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="phone" // Changed name to phone for PhoneInput
              label="Phone Number"
              rules={[{ validator: validatePhone }]}
            >
              <PhoneInput
                style={{ borderRadius: "0px" }}
                placeholder="Enter phone number"
                value={phoneValue}
                onChange={setPhoneValue}
                defaultCountry="IN"
                className="phone-input"
              />
            </Form.Item>
          )}

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: "Please enter your password" },
              { min: 8, message: "Password must be at least 8 characters" },
            ]}
          >
            <Input.Password
              style={{ borderRadius: "0px" }}
              prefix={<LockOutlined />}
              placeholder="Enter your password"
            />
          </Form.Item>

          <Form.Item className="auth-submit">
            <Button
              style={{ borderRadius: "0px" }}
              type="primary"
              htmlType="submit"
              loading={isLoading}
              block
              className="auth-button"
            >
              {isLoading ? "Signing In..." : "Sign In"}
            </Button>
          </Form.Item>
        </Form>

        <div className="auth-footer">
          <Space direction="vertical" align="center" className="w-100">
            <Text>
              Don't have an account?{" "}
              <Link to="/signup" className="auth-link">
                Sign Up
              </Link>
            </Text>
            <Link to="/forgot-password" className="auth-link">
              Forgot your password?
            </Link>
            <Text
              className="auth-link instructions-trigger"
              onClick={() => {
                setShowInstructions(true);
                // Don't update sessionStorage when manually triggered
              }}
              style={{ cursor: "pointer", marginTop: "8px" }}
            >
              Need help? View instructions
            </Text>
          </Space>
        </div>
      </Card>

      <Modal
        title={
          <div className="modal-title-center">
            <Text strong className="modal-main-title">
              സുപ്രഭാതം ഇ പേപ്പർ !
            </Text>
            <Text className="modal-subtitle">
              ഇപ്പോൾ സബ്സ്ക്രിപ്ഷൻ നേടൂ!
            </Text>
          </div>
        }
        open={showInstructions}
        onCancel={() => setShowInstructions(false)}
        footer={null}
        className="login-instructions-modal"
        width={760}
        centered
      >
        <div className="modal-welcome-content">
          <div className="modal-columns">
            <div className="modal-col modal-col-left">
              <div className="welcome-message">
                <Text className="welcome-text">
                  നിങ്ങളുടെ സുപ്രഭാതം ePaper വായിക്കാൻ ഇപ്പോൾത്തന്നെ
                  രജിസ്റ്റർ ചെയ്യൂ.
                </Text>
              </div>

              <div className="steps-section">
                <Text strong className="steps-title">
                  ചെയ്യേണ്ടത് ഇത്രമാത്രം:
                </Text>

                <div className="step-item">
                  <Text className="step-number">1.</Text>
                  <Text className="step-text">
                    Sign Up ബട്ടൺ ക്ലിക്ക് ചെയ്യുക.
                  </Text>
                </div>

                <div className="step-item">
                  <Text className="step-number">2.</Text>
                  <Text className="step-text">
                    നിങ്ങളുടെ പേര്, ഇമെയിൽ, ഫോൺ നമ്പർ എന്നിവ നൽകി
                    പാസ്സ്‌വേർഡ് സെറ്റ് ചെയ്ത് രജിസ്റ്റർ ചെയാം.
                  </Text>
                </div>

                <div className="step-item">
                  <Text className="step-number">3.</Text>
                  <Text className="step-text">
                    ശേഷം, ലോഗിൻ ചെയ്ത് ePaper വായിച്ചു തുടങ്ങാം.
                  </Text>
                </div>
              </div>
            </div>

            <div className="modal-col modal-col-right">
              <div className="queries-section">
                <Text strong className="steps-title">
                  Queries:
                </Text>
                <Text className="step-text">
                  For subscription enquiries or any other queries, reach us
                  at:
                </Text>
                <div className="contact-numbers">
                  <Text className="step-text">
                    <a href="tel:00971565157220">0097156 5157220</a>
                  </Text>
                  <Text className="step-text">
                    <a href="tel:00971502787006">0097150 278 7006</a>
                  </Text>
                  <Text className="step-text">
                    <a href="tel:+918281639008">+91 82816 39008</a>
                  </Text>
                </div>
              </div>

              <div className="support-section">
                <Text strong className="steps-title">
                  Technical Support:
                </Text>
                <Text className="step-text">
                  For any assistance, feel free to contact us at:{" "}
                  <a href="tel:+918075415365">+91 8075415365</a>
                </Text>
                <Text className="step-text" style={{ marginTop: "4px" }}>
                  Thank you for choosing us!
                </Text>
              </div>
            </div>
          </div>

          <div className="app-download-section">
            <Text strong className="steps-title">
              Download Our Mobile App
            </Text>
            <Text className="step-text">
              Get the full experience on your mobile device
            </Text>
            <div className="app-store-buttons">
              <a
                href="https://apps.apple.com/in/app/gulf-suprabhaatham/id6756600940"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                  alt="Download on the App Store"
                  className="store-badge"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.gulfsuprabhaatham"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                  alt="Get it on Google Play"
                  className="store-badge"
                />
              </a>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LoginForm;
